import { useCallback, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  invalidateProductMasterdata,
  markProductMasterdataStale,
  PartnerProductDTO,
  useCreateProductMasterdata,
  useDeleteProductMasterdata,
  useUpdateProductMasterdata,
} from 'services/repositories/masterdata/masterdata';
import { NotificationType, useNotificationStore } from 'stores/notifications/notifications';
import { processSubmissionErrors, ValidationError } from 'support/helpers/errors/errors';
import { ColumnTypesInternal } from 'support/types/webediTable';

import {
  buildProductMasterdataSavePlan,
  InvalidNewRowField,
  ProductMasterdataFormValues,
  ProductRow,
  PRODUCTS_DATA_PREFIX,
  SavePlan,
} from '../helpers/productRows';
import { mapProductValidationErrors, ValidationRequest } from '../helpers/productValidationErrors';
import { ProductValidationService } from '../helpers/ProductValidationService';

const FIELD_LABEL_KEYS: Record<InvalidNewRowField, string> = {
  unit_price: 'settings:productMasterdata.table.headers.unitPrice',
  unit_of_measure: 'settings:productMasterdata.table.headers.unitOfMeasure',
  currency: 'settings:productMasterdata.table.headers.currency',
};

/**
 * A save is up to three requests, sent in this order:
 *
 * 1. **PATCH** — the edits.
 * 2. **POST** — the new products.
 * 3. **DELETE** — last, and deliberately: it is the one step that cannot be taken back. Validation
 *    is where a save is likely to fail, and if a rejected edit or new product came after the
 *    delete, the user would be told to fix their data with products already gone. Deleting only
 *    once everything else has been accepted means a failure leaves nothing deleted.
 *
 * (The price of that order: an identifier cannot move from a deleted product to another in the
 * same save, because the deleted product still holds it when the other one is validated.)
 *
 * Each request is atomic, but the three are not atomic together, so a failure part-way leaves the
 * server partly saved while the user is still in edit mode with everything unsaved on screen. The
 * user's remedy is to press Save again, which has to be safe:
 * - PATCH is idempotent — it re-sends the same values;
 * - POST is not, so a product it created stops being a new row the moment the server answers (it
 *   takes the server's id, see `markCreated`) and is not created a second time;
 * - DELETE ids that already went through are remembered and left out of the retry (re-sending one
 *   would 404 the whole batch); that only matters if the refetch after the delete fails.
 *
 * The mutations don't refetch on their own (see `invalidateProductMasterdata`); this hook does it
 * once, after the last step — and, if the user cancels a half-saved edit, when the session ends.
 * After each step it only marks the list stale (`markProductMasterdataStale`), so a user who leaves
 * the page instead gets a refetch on their next visit.
 *
 * A 422 keys each error by the product's position in the request that was sent
 * (`products.1.unit_price`), and only edited rows are sent, so that position is not the table's.
 * The save plan remembers which table row each payload entry came from, which is what lets an
 * error be shown on its row and cell (`showFailure`). Whatever can't be placed that way — and any
 * failure that isn't a 422 — goes to the banner (`defaultData: undefined` short-circuits
 * `processSubmissionErrors`'s field-routing branch, which is about form fields, not table rows).
 */
export const useProductMasterdataSave = (
  formMethods: Pick<UseFormReturn<ProductMasterdataFormValues>, 'getValues' | 'setValue'>,
  baselineRows: Array<ProductRow>,
  onSaved: () => void,
  validation: Pick<ProductValidationService, 'showErrors' | 'clearErrors'>,
) => {
  const { t } = useTranslation();
  const { addNotification } = useNotificationStore();
  const { mutateAsync: updateProducts } = useUpdateProductMasterdata();
  const { mutateAsync: createProducts } = useCreateProductMasterdata();
  const { mutateAsync: deleteProducts } = useDeleteProductMasterdata();

  const [isSaving, setIsSaving] = useState(false);
  const [nonInputErrors, setNonInputErrors] = useState<Array<string>>([]);
  // The plan a confirmation dialog is holding while the user decides whether to delete, with the
  // rows it was built from.
  const [pending, setPending] = useState<{ plan: SavePlan; rows: Array<ProductRow> } | undefined>();
  const pendingPlan = pending?.plan;

  const deletedIds = useRef(new Set<string>());
  // True once any request of this edit went through without the query being refetched after it.
  const hasUnsyncedProgress = useRef(false);

  // The server has changed and the list has not been refetched. Mark it stale right away so that
  // leaving the page (rather than retrying or cancelling) can't leave the stale rows in the cache;
  // `endSession` does the actual refetch when the user cancels back to view mode.
  const recordProgress = useCallback(() => {
    hasUnsyncedProgress.current = true;
    void markProductMasterdataStale();
  }, []);

  /**
   * The server has created these, in the order they were sent; the rows they came from are saved
   * products now. Giving them the server's id and dropping the "added" marker is what stops a retry
   * (after a later step failed) from creating them again — a new row is exactly one with no id.
   */
  const markCreated = useCallback(
    (plan: SavePlan, created: Array<PartnerProductDTO> | undefined) => {
      if (!Array.isArray(created) || created.length !== plan.createRowIndexes.length) {
        return;
      }
      created.forEach((product, position) => {
        const rowIndex = plan.createRowIndexes[position];
        formMethods.setValue(`${PRODUCTS_DATA_PREFIX}.${rowIndex}.id`, product.id);
        formMethods.setValue(`${PRODUCTS_DATA_PREFIX}.${rowIndex}._internal`, {
          [ColumnTypesInternal.procuros_added]: false,
        });
      });
    },
    [formMethods],
  );

  const idsStillToDelete = useCallback(
    (plan: SavePlan) => plan.deleteIds.filter((id) => !deletedIds.current.has(id)),
    [],
  );

  /**
   * A 422 from the batch endpoints scopes each error to the product it is about (`products.1.…`).
   * Those go to the rows and cells they belong to; whatever can't be placed — and every other kind
   * of failure — goes to the banner, because it can't be attributed to anything on screen.
   */
  const showFailure = useCallback(
    (error: Error, request: ValidationRequest | undefined, rows: Array<ProductRow>) => {
      const mapped =
        error instanceof ValidationError && request
          ? mapProductValidationErrors(error.errors, request, rows)
          : undefined;

      if (!mapped || mapped.items.length === 0) {
        processSubmissionErrors<undefined>({
          error,
          setInputError: undefined,
          setNonInputErrors,
          defaultData: undefined,
          t,
        });
        return;
      }

      // The banner above the table counts and cycles through these; only what couldn't be placed
      // is listed in the generic error box.
      validation.showErrors(mapped.items);
      setNonInputErrors(Object.values(mapped.unmapped).flat());
    },
    [validation, t],
  );

  const send = useCallback(
    async (plan: SavePlan, rows: Array<ProductRow>) => {
      setIsSaving(true);
      // Which of the requests is in flight, so a 422 can be traced back to the rows it was about.
      let failing: ValidationRequest | undefined;
      try {
        if (plan.updates.length > 0) {
          failing = { products: plan.updates, rowIndexes: plan.updateRowIndexes };
          await updateProducts({ products: plan.updates });
          failing = undefined;
          recordProgress();
        }
        if (plan.creates.length > 0) {
          failing = { products: plan.creates, rowIndexes: plan.createRowIndexes };
          const created = await createProducts({ products: plan.creates });
          failing = undefined;
          markCreated(plan, created);
          recordProgress();
        }

        const ids = idsStillToDelete(plan);
        if (ids.length > 0) {
          await deleteProducts({ ids });
          ids.forEach((id) => deletedIds.current.add(id));
          recordProgress();
        }

        await invalidateProductMasterdata();
        hasUnsyncedProgress.current = false;
        addNotification(t('settings:productMasterdata.onSuccess'), NotificationType.success);
        onSaved();
      } catch (error) {
        const reason = error instanceof Error ? error : new Error(String(error));
        showFailure(reason, failing, rows);
      } finally {
        setIsSaving(false);
      }
    },
    [
      idsStillToDelete,
      markCreated,
      recordProgress,
      showFailure,
      deleteProducts,
      updateProducts,
      createProducts,
      addNotification,
      onSaved,
      t,
    ],
  );

  const save = useCallback(() => {
    setNonInputErrors([]);
    validation.clearErrors();

    const rows = formMethods.getValues(PRODUCTS_DATA_PREFIX);
    const plan = buildProductMasterdataSavePlan(rows, baselineRows);

    if (plan.invalidNewRows.length > 0) {
      setNonInputErrors(
        plan.invalidNewRows.map(({ rowNumber, fields }) =>
          t('settings:productMasterdata.validation.newRowMissingFields', {
            row: rowNumber,
            fields: fields.map((field) => t(FIELD_LABEL_KEYS[field])).join(', '),
          }),
        ),
      );
      return;
    }

    if (plan.deleteIds.length + plan.updates.length + plan.creates.length === 0) {
      onSaved();
      return;
    }

    // Deleting is permanent (it also pulls the product out of order forms and bundles), so it is
    // the one thing a save asks about first. Ids a failed earlier attempt already deleted don't
    // count — the user has confirmed those once.
    if (idsStillToDelete(plan).length > 0) {
      setPending({ plan, rows });
      return;
    }

    void send(plan, rows);
  }, [formMethods, baselineRows, idsStillToDelete, send, onSaved, validation, t]);

  const confirmDelete = useCallback(() => {
    if (!pending) {
      return;
    }
    setPending(undefined);
    void send(pending.plan, pending.rows);
  }, [pending, send]);

  const cancelDelete = useCallback(() => setPending(undefined), []);

  /**
   * Called whenever edit mode ends, for any reason. Drops the banner and the bookkeeping for this
   * edit — and if a failed save had already changed the server, refetches so view mode doesn't
   * show products that no longer exist (or miss ones that were created). It can't refetch earlier:
   * doing so mid-edit would change the rows under the user's unsaved changes.
   */
  const endSession = useCallback(() => {
    setNonInputErrors([]);
    validation.clearErrors();
    setPending(undefined);
    deletedIds.current.clear();
    if (hasUnsyncedProgress.current) {
      hasUnsyncedProgress.current = false;
      void invalidateProductMasterdata();
    }
  }, [validation]);

  return {
    save,
    isSaving,
    nonInputErrors,
    endSession,
    pendingDeleteCount: pendingPlan ? idsStillToDelete(pendingPlan).length : 0,
    isConfirmingDelete: pending !== undefined,
    confirmDelete,
    cancelDelete,
  };
};

/**
 * i18n keys
 * t('settings:productMasterdata.table.headers.unitPrice')
 * t('settings:productMasterdata.table.headers.unitOfMeasure')
 * t('settings:productMasterdata.table.headers.currency')
 */
